import { useState, useCallback } from 'react';
import { rlService } from '../services/rlService';
import { analyzeQuestion } from '../utils/questionAnalysis';

export const useRLFeedback = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submitQuestion = useCallback(async (
    question: string,
    mode: 'default' | 'finance' | 'education'
  ) => {
    try {
      // Analyze question for learning
      const analysis = await analyzeQuestion(question);
      
      // Create experience from question
      await rlService.addExperience({
        state: analysis.features,
        action: mode,
        reward: 0, // Initial neutral reward
        next_state: analysis.features,
        metadata: {
          mode,
          questionType: analysis.type,
          complexity: analysis.complexity,
          topics: analysis.topics,
          timestamp: new Date().toISOString()
        }
      });
    } catch (err) {
      console.error('Error processing question for RL:', err);
    }
  }, []);

  const submitFeedback = useCallback(async (
    actionId: string,
    reward: number,
    context?: Record<string, any>
  ) => {
    setIsSubmitting(true);
    setError(null);

    try {
      await rlService.submitFeedback({
        action_id: actionId,
        reward,
        context: {
          ...context,
          timestamp: new Date().toISOString()
        }
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit feedback');
      throw err;
    } finally {
      setIsSubmitting(false);
    }
  }, []);

  const addExperience = useCallback(async (
    state: number[],
    action: string,
    reward: number,
    nextState: number[]
  ) => {
    setIsSubmitting(true);
    setError(null);

    try {
      await rlService.addExperience({
        state,
        action,
        reward,
        next_state: nextState,
        metadata: {
          timestamp: new Date().toISOString()
        }
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add experience');
      throw err;
    } finally {
      setIsSubmitting(false);
    }
  }, []);

  return {
    submitFeedback,
    addExperience,
    submitQuestion,
    isSubmitting,
    error,
  };
};